import React from "react";
import { useStorefrontOverrides } from "@/hooks/useStorefrontOverrides";

/**
 * Sunrise Community — Optimized Site Plan · Stacks (Option A)
 * 6 parallel N-S residential bars (B1-B6) · 192 units total · 3-floor max
 * Split commercial plaza along the south with a CENTRAL GATEWAY
 * Covered pavilion + laundry · landscaped garden strips between bars
 * Perimeter golf-cart parking on east and west
 *
 * Lot survey:
 *   57 ft street neck · 280 ft south edge · 308–331 ft sides · ~2.13 acres
 */

const SCALE = 1.35;
const LOT_VERTS = [
  [0, 0], [0, 331], [171, 331], [171, 408],
  [228, 408], [228, 329], [284, 329], [284, 21], [0, 0],
];
const LOT_W = 284;
const LOT_H = 408;

// 6 stack bars (each split into 2 segments by a mid-bar breezeway gap)
const BAR_COUNT = 6;
const SEG1_LEN = 120;
const BREEZEWAY = 16;
const SEG2_LEN = 160;
const BAR_LEN = SEG1_LEN + BREEZEWAY + SEG2_LEN;   // 296 ft total
const BAR_DEPTH = 8;
const BAR_PITCH = 42;
const BAR_X0 = 22;
const BAR_Z0 = 10;
const BAR_N_END = BAR_Z0 + BAR_LEN;      // 306

// Single retail strip — ONE continuous commercial building at the entrance.
const COMM_WING_W = 8 * 32;             // 256 ft total — 8 storefronts in a row
const GATE_W = 50;                       // gateway arch width (placed in the road neck)
const COMM_TOTAL = COMM_WING_W;
const ARRIVAL_PLAZA_DEPTH = 14;          // arrival/drop-off plaza between bars and retail
const COMM_Z = BAR_N_END + ARRIVAL_PLAZA_DEPTH;     // z = 320 (sits in wide part, just south of road neck at z=329)
const COMM_X0 = (LOT_W - COMM_TOTAL) / 2;

const COMM_COLORS_DEFAULT = ["#fb7185", "#fbbf24", "#84cc16", "#22d3ee", "#a78bfa", "#f97316", "#10b981", "#3b82f6"];
const COMM_LABELS_DEFAULT = ["COFFEE", "BAKERY", "MARKET", "DELI", "PHARMACY", "BARBER", "CAFE", "LAUNDRY"];

// Amenities — in the arrival plaza zone between bars and the retail strip
const PAVILION = { x: LOT_W / 2 - 30, y: BAR_N_END + 2, w: 60, h: 10 };
const LAUNDRY = { x: BAR_X0 + 6 * BAR_PITCH - 50, y: BAR_N_END + 2, w: 50, h: 8 };
// Arrival drop-off — wide band the full length of the bar row, just south of retail
const DROP_OFF = { x: BAR_X0, y: BAR_N_END + 2, w: 6 * BAR_PITCH + 4, h: ARRIVAL_PLAZA_DEPTH - 4 };

// Retail parking — in the road neck north of the retail strip (visitor parking)
const COMM_PARK_FRONT = { id: "comm-park-front", x: 171, y: COMM_Z + 10, w: 57, h: 50, count: 41 };

// Residential perimeter cart parking — west + east edges of the wide portion
const PARKING_WEST = { id: "park-west",  x: 4,                              y: BAR_Z0, w: 14, h: BAR_LEN, count: 26 };
const PARKING_EAST = { id: "park-east",  x: BAR_X0 + BAR_COUNT * BAR_PITCH + 2, y: BAR_Z0, w: 14, h: BAR_LEN, count: 26 };

export default function SitePlan2D({ siteSummary, showEgress }) {
  const stores = useStorefrontOverrides();
  const COMM_COLORS = stores.map((s) => s.color);
  const COMM_LABELS = stores.map((s) => s.name);
  const margin = 70;
  // Extra right-side padding so the bottom-right PARKING card isn't clipped.
  const W = LOT_W * SCALE + margin * 2 + 200;
  const H = LOT_H * SCALE + margin * 2 + 100;
  const f = (n) => n * SCALE;
  const tx = (n) => margin + 60 + f(n);
  const ty = (n) => margin + f(LOT_H - n);

  const lotPath = LOT_VERTS.map(([x, y], i) =>
    `${i === 0 ? "M" : "L"} ${tx(x)} ${ty(y)}`).join(" ") + " Z";

  const totalRes = siteSummary?.total_residential ?? 192;
  const totalComm = siteSummary?.total_commercial ?? 8;
  const totalContainers = siteSummary?.total_containers ?? 134;
  const livingSF = siteSummary?.total_living_sf ?? 40320;
  const parkingReq = siteSummary?.parking_required ?? 93;
  const stripStalls = PARKING_WEST.count + PARKING_EAST.count + COMM_PARK_FRONT.count;
  const parkingMet = stripStalls >= parkingReq;

  // Draw one stack bar (residential) — TWO segments + breezeway gap mid-bar
  const drawBar = (i) => {
    const bx = BAR_X0 + i * BAR_PITCH;
    const cx = bx + BAR_DEPTH / 2;
    const cy = BAR_Z0 + BAR_LEN / 2;
    const segNorthZ = BAR_Z0 + SEG1_LEN + BREEZEWAY;
    return (
      <g key={`bar-${i}`} data-testid={`site-bar-B${i + 1}`}>
        {/* SOUTH segment */}
        <rect x={tx(bx)} y={ty(BAR_Z0 + SEG1_LEN)}
              width={f(BAR_DEPTH)} height={f(SEG1_LEN)}
              fill="#f8fafc" stroke="#facc15" strokeWidth="1.5" />
        {/* NORTH segment */}
        <rect x={tx(bx)} y={ty(segNorthZ + SEG2_LEN)}
              width={f(BAR_DEPTH)} height={f(SEG2_LEN)}
              fill="#f8fafc" stroke="#facc15" strokeWidth="1.5" />

        {/* BREEZEWAY GAP — visually marked with hatching */}
        <rect x={tx(bx - 2)} y={ty(segNorthZ)}
              width={f(BAR_DEPTH + 4)} height={f(BREEZEWAY)}
              fill="#0a1a0f" stroke="#22c55e" strokeWidth="0.8"
              strokeDasharray="2 2" opacity="0.7" />

        {/* Continuous catwalk on west side (split by breezeway gap as well) */}
        <rect x={tx(bx - 5)} y={ty(BAR_Z0 + SEG1_LEN)}
              width={f(5)} height={f(SEG1_LEN)}
              fill="#475569" fillOpacity="0.55" />
        <rect x={tx(bx - 5)} y={ty(segNorthZ + SEG2_LEN)}
              width={f(5)} height={f(SEG2_LEN)}
              fill="#475569" fillOpacity="0.55" />

        {/* Stair pads at outer ends of each segment */}
        <rect x={tx(bx - 7)} y={ty(BAR_Z0 + 12)}
              width={f(7)} height={f(12)}
              fill="#0f172a" stroke="#facc15" strokeWidth="1" />
        <rect x={tx(bx - 7)} y={ty(segNorthZ + SEG2_LEN)}
              width={f(7)} height={f(12)}
              fill="#0f172a" stroke="#facc15" strokeWidth="1" />

        {/* Building number label (above breezeway) */}
        <text x={tx(cx)} y={ty(segNorthZ + BREEZEWAY / 2)} textAnchor="middle"
              fill="#22c55e" fontSize="9" letterSpacing="3"
              fontFamily="monospace" fontWeight="700">
          B{i + 1}
        </text>
      </g>
    );
  };

  // Draw 8 commercial storefronts as ONE continuous retail strip
  const drawCommercial = () => {
    const cw = COMM_WING_W / 8;          // 32 ft per storefront
    return (
      <g data-testid="commercial-plaza">
        {/* Strip backdrop — one continuous building */}
        <rect x={tx(COMM_X0) - 2} y={ty(COMM_Z + 8) - 2}
              width={f(COMM_WING_W) + 4} height={f(8) + 4}
              fill="#0f172a" stroke="#facc15" strokeWidth="1.4"
              rx="2" />
        {/* 8 storefronts in a row */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <g key={`store-${i}`}>
            <rect x={tx(COMM_X0 + i * cw)} y={ty(COMM_Z + 8)}
                  width={f(cw)} height={f(8)}
                  fill={COMM_COLORS[i]} fillOpacity="0.85"
                  stroke="#0f172a" strokeWidth="0.8" />
            <text x={tx(COMM_X0 + i * cw + cw / 2)} y={ty(COMM_Z + 3)}
                  textAnchor="middle" fill="#0f172a" fontSize="6.5"
                  letterSpacing="1.2" fontFamily="monospace" fontWeight="700">
              {COMM_LABELS[i]}
            </text>
          </g>
        ))}
        {/* "SUNRISE RETAIL" label on the strip */}
        <text x={tx(COMM_X0 + COMM_WING_W / 2)} y={ty(COMM_Z + 14)}
              textAnchor="middle" fill="#facc15" fontSize="9"
              letterSpacing="3" fontFamily="monospace" fontWeight="700">
          SUNRISE RETAIL STRIP · 8 STOREFRONTS · GROUND-FLOOR
        </text>
      </g>
    );
  };

  // Road neck spans x=171..228 (57 ft wide, centered at x=199.5).
  // The gateway arch must align with the actual road entry, not the lot center.
  const ROAD_NECK_CX = 199.5;
  const gateX = ROAD_NECK_CX - GATE_W / 2;

  return (
    <div data-testid="site-plan" className="w-full overflow-auto bg-[#070b10] p-4">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block">
        <defs>
          <pattern id="site-grid-1" width={SCALE * 5} height={SCALE * 5} patternUnits="userSpaceOnUse">
            <path d={`M ${SCALE * 5} 0 L 0 0 0 ${SCALE * 5}`} fill="none" stroke="#0f1a2a" strokeWidth="0.4" />
          </pattern>
          <pattern id="site-grid-2" width={SCALE * 20} height={SCALE * 20} patternUnits="userSpaceOnUse">
            <rect width={SCALE * 20} height={SCALE * 20} fill="url(#site-grid-1)" />
            <path d={`M ${SCALE * 20} 0 L 0 0 0 ${SCALE * 20}`} fill="none" stroke="#1e293b" strokeWidth="0.6" />
          </pattern>
          <pattern id="grass-site" width="22" height="22" patternUnits="userSpaceOnUse">
            <rect width="22" height="22" fill="#0a1a0f" />
            <circle cx="4" cy="4" r="1.4" fill="#15803d" opacity="0.6" />
            <circle cx="14" cy="10" r="1.4" fill="#16a34a" opacity="0.6" />
            <circle cx="8" cy="16" r="1.4" fill="#15803d" opacity="0.6" />
          </pattern>
          <pattern id="paving" width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill="#1c1917" />
            <line x1="0" y1="0" x2="14" y2="0" stroke="#292524" strokeWidth="0.6" />
            <line x1="0" y1="0" x2="0" y2="14" stroke="#292524" strokeWidth="0.6" />
          </pattern>
        </defs>

        <rect width={W} height={H} fill="#070b10" />
        <rect width={W} height={H} fill="url(#site-grid-2)" opacity="0.5" />

        {/* Title — anchored to the top-left, sized to fit alongside the 57-ft neck label */}
        <text x={margin} y={28} fill="#facc15" fontFamily="monospace" fontSize="12" letterSpacing="3">
          OPTIMIZED SITE PLAN · SUNRISE COMMUNITY
        </text>
        <text x={margin} y={44} fill="#94a3b8" fontFamily="monospace" fontSize="9" letterSpacing="2">
          SAN PEDRO, BELIZE · 2.13 ACRES · 6 BARS + RETAIL STRIP · {totalRes} RES (31% 2BR) + {totalComm} COMM · {totalContainers} CONTAINERS · {livingSF.toLocaleString()} SF LIVING
        </text>
        <text x={W - margin} y={28} textAnchor="end" fill="#5b6776" fontFamily="monospace" fontSize="10" letterSpacing="2">
          SCALE 1" = 40'-0"
        </text>

        {/* Lot perimeter */}
        <path d={lotPath} fill="#0a1a0f" fillOpacity="0.8" stroke="#fb923c" strokeWidth="2.5" />
        <path d={lotPath} fill="url(#grass-site)" opacity="0.45" />

        <g fontFamily="monospace" fontSize="9" fill="#fb923c" fontWeight="700">
          {/* 57 FT NECK — centered on the actual road neck (x=199.5), placed INSIDE the lot above the parking */}
          <text x={tx(199.5)} y={ty(404)} textAnchor="middle">57 FT NECK</text>
          <text x={tx(-12)} y={ty(165)} textAnchor="middle"
                transform={`rotate(-90 ${tx(-12)} ${ty(165)})`}>331 FT</text>
          <text x={tx(296)} y={ty(165)} textAnchor="middle"
                transform={`rotate(-90 ${tx(296)} ${ty(165)})`}>308 FT</text>
        </g>

        {/* Landscaped garden strips between bars */}
        {Array.from({ length: BAR_COUNT - 1 }).map((_, i) => {
          const gx = BAR_X0 + 8 + i * BAR_PITCH;
          const gw = BAR_PITCH - 8;
          return (
            <rect key={`g-${i}`} x={tx(gx)} y={ty(BAR_Z0 + BAR_LEN - 10)}
                  width={f(gw)} height={f(BAR_LEN - 20)}
                  fill="url(#grass-site)" stroke="#15803d" strokeWidth="0.5"
                  strokeDasharray="2 2" opacity="0.6" />
          );
        })}

        {/* Perimeter parking strips */}
        {[PARKING_WEST, PARKING_EAST].map((p) => (
          <g key={p.id}>
            <rect x={tx(p.x)} y={ty(p.y + p.h)} width={f(p.w)} height={f(p.h)} fill="url(#paving)" />
            {Array.from({ length: p.count }).map((_, i) => {
              const isEV = i % 5 === 0;
              const sh = (p.h - 4) / p.count;
              return (
                <rect key={`${p.id}-stall-${i}`}
                  x={tx(p.x + 2)} y={ty(p.y + p.h - 2 - i * sh)}
                  width={f(p.w - 4)} height={f(sh - 0.4)}
                  fill={isEV ? "#064e3b" : "#0f172a"}
                  stroke={isEV ? "#22c55e" : "#475569"} strokeWidth="0.4" />
              );
            })}
            <text x={tx(p.x + p.w / 2)} y={ty(p.y + p.h + 6)}
                  textAnchor="middle" fill="#7dd3fc" fontSize="7"
                  letterSpacing="2" fontFamily="monospace">
              {p.count} CART
            </text>
          </g>
        ))}

        {/* Retail parking — IN THE ROAD NECK, north of the retail strip */}
        {[COMM_PARK_FRONT].map((p) => (
          <g key={p.id}>
            <rect x={tx(p.x)} y={ty(p.y + p.h)} width={f(p.w)} height={f(p.h)} fill="url(#paving)" />
            {Array.from({ length: p.count }).map((_, i) => {
              const isEV = i % 6 === 0;
              const cols = 4;
              const rows = Math.ceil(p.count / cols);
              const col = i % cols;
              const row = Math.floor(i / cols);
              const sw = (p.w - 4) / cols;
              const sh = (p.h - 4) / rows;
              return (
                <rect key={`${p.id}-stall-${i}`}
                  x={tx(p.x + 2 + col * sw)} y={ty(p.y + p.h - 2 - row * sh)}
                  width={f(sw - 0.4)} height={f(sh - 0.4)}
                  fill={isEV ? "#064e3b" : "#0f172a"}
                  stroke={isEV ? "#22c55e" : "#475569"} strokeWidth="0.4" />
              );
            })}
            <text x={tx(p.x + p.w / 2)} y={ty(p.y + p.h + 6)}
                  textAnchor="middle" fill="#fb923c" fontSize="7"
                  letterSpacing="2" fontFamily="monospace">
              RETAIL PARKING · {p.count}
            </text>
          </g>
        ))}

        {/* === ONE CONTINUOUS RETAIL STRIP at the entrance === */}
        {drawCommercial()}

        {/* === GATEWAY ARCH (in the road neck, north of retail strip) === */}
        <g data-testid="gateway-arch">
          {/* The drive/walk-through */}
          <rect x={tx(gateX)} y={ty(COMM_Z + 8)}
                width={f(GATE_W)} height={f(8)}
                fill="#0f172a" stroke="#fbbf24" strokeWidth="1.5"
                strokeDasharray="4 3" />
          {/* Arch posts */}
          <rect x={tx(gateX) - 2} y={ty(COMM_Z + 8) - 4}
                width="4" height={f(8) + 8} fill="#fbbf24" />
          <rect x={tx(gateX + GATE_W) - 2} y={ty(COMM_Z + 8) - 4}
                width={4} height={f(8) + 8} fill="#fbbf24" />
          {/* Sign band — above commercial (pointing toward street/north) */}
          <rect x={tx(gateX) - 2} y={ty(COMM_Z + 22)}
                width={f(GATE_W) + 4} height="12"
                fill="#fbbf24" />
          <text x={tx(gateX + GATE_W / 2)} y={ty(COMM_Z + 22) + 8}
                textAnchor="middle" fill="#0f172a" fontSize="9"
                letterSpacing="3" fontFamily="monospace" fontWeight="900">
            SUNRISE COMMUNITY
          </text>
          {/* MAIN ENTRANCE marker — points NORTH toward the street */}
          <text x={tx(gateX + GATE_W / 2)} y={ty(COMM_Z + 36)}
                textAnchor="middle" fill="#fde68a" fontSize="7"
                letterSpacing="3" fontFamily="monospace">
            ↑ MAIN ENTRANCE (FROM ROAD) ↑
          </text>
        </g>

        {/* === DROP-OFF / ARRIVAL PLAZA just inside the gate === */}
        <g data-testid="drop-off">
          <rect x={tx(DROP_OFF.x)} y={ty(DROP_OFF.y + DROP_OFF.h)}
                width={f(DROP_OFF.w)} height={f(DROP_OFF.h)}
                fill="#3f3f46" fillOpacity="0.5" stroke="#94a3b8"
                strokeWidth="0.6" strokeDasharray="3 2" />
          <text x={tx(DROP_OFF.x + DROP_OFF.w / 2)} y={ty(DROP_OFF.y + DROP_OFF.h / 2)}
                textAnchor="middle" fill="#cbd5e1" fontSize="7"
                letterSpacing="3" fontFamily="monospace" fontWeight="700">
            ARRIVAL PLAZA
          </text>
        </g>

        {/* === COVERED PAVILION (town-square just inside the gateway) === */}
        <g data-testid="pavilion">
          <rect x={tx(PAVILION.x)} y={ty(PAVILION.y + PAVILION.h)}
                width={f(PAVILION.w)} height={f(PAVILION.h)}
                fill="#1e293b" stroke="#fbbf24" strokeWidth="1.5" />
          <text x={tx(PAVILION.x + PAVILION.w / 2)} y={ty(PAVILION.y + PAVILION.h / 2 + 1)}
                textAnchor="middle" fill="#fbbf24" fontSize="9"
                letterSpacing="3" fontFamily="monospace" fontWeight="700">
            COVERED PAVILION
          </text>
          <text x={tx(PAVILION.x + PAVILION.w / 2)} y={ty(PAVILION.y + PAVILION.h / 2 - 8)}
                textAnchor="middle" fill="#fde68a" fontSize="6.5"
                letterSpacing="2" fontFamily="monospace">
            TOWN SQUARE · RESTAURANT
          </text>
        </g>

        {/* === LAUNDRY === */}
        <g data-testid="laundry-block">
          <rect x={tx(LAUNDRY.x)} y={ty(LAUNDRY.y + LAUNDRY.h)}
                width={f(LAUNDRY.w)} height={f(LAUNDRY.h)}
                fill="#0e7490" stroke="#22d3ee" strokeWidth="1.2" />
          <text x={tx(LAUNDRY.x + LAUNDRY.w / 2)} y={ty(LAUNDRY.y + LAUNDRY.h / 2 + 1)}
                textAnchor="middle" fill="#67e8f9" fontSize="8"
                letterSpacing="2" fontFamily="monospace" fontWeight="700">
            LAUNDRY HOUSE
          </text>
        </g>

        {/* === 6 STACK BARS === */}
        {Array.from({ length: BAR_COUNT }).map((_, i) => drawBar(i))}

        {/* North arrow */}
        <g transform={`translate(${margin + 28}, ${margin + 34})`}>
          <circle cx="0" cy="0" r="20" fill="#0b0f14" stroke="#facc15" strokeWidth="1.2" />
          <polygon points="0,-14 5,3 0,0 -5,3" fill="#facc15" />
          <text y="-22" textAnchor="middle" fill="#facc15" fontSize="10" letterSpacing="3" fontFamily="monospace" fontWeight="700">N</text>
        </g>

        {/* PROJECT TOTALS card — two rows, all KPIs equally prominent */}
        <g transform={`translate(${margin + 16}, ${H - margin - 138})`}>
          <rect width="380" height="158" fill="#0f172a" stroke="#facc15" strokeWidth="1.5" />
          <text x="14" y="22" fill="#facc15" fontSize="10" letterSpacing="3" fontFamily="monospace">PROJECT TOTALS</text>

          {/* ROW 1: Residential · Storefronts · Bars · Floors */}
          <text x="14"  y="56" fill="#fbbf24" fontSize="22" fontWeight="700" fontFamily="monospace">{totalRes}</text>
          <text x="14"  y="70" fill="#94a3b8" fontSize="9"  fontFamily="monospace">RESIDENTIAL</text>

          <text x="106" y="56" fill="#fb923c" fontSize="22" fontWeight="700" fontFamily="monospace">{totalComm}</text>
          <text x="106" y="70" fill="#94a3b8" fontSize="9"  fontFamily="monospace">STOREFRONTS</text>

          <text x="196" y="56" fill="#22c55e" fontSize="22" fontWeight="700" fontFamily="monospace">6</text>
          <text x="196" y="70" fill="#94a3b8" fontSize="9"  fontFamily="monospace">BARS</text>

          <text x="266" y="56" fill="#a78bfa" fontSize="22" fontWeight="700" fontFamily="monospace">3</text>
          <text x="266" y="70" fill="#94a3b8" fontSize="9"  fontFamily="monospace">FLOORS</text>

          <line x1="14" y1="84" x2="366" y2="84" stroke="#334155" strokeWidth="0.6" />

          {/* ROW 2: Containers · Living SF · 2BR mix */}
          <text x="14"  y="118" fill="#7dd3fc" fontSize="22" fontWeight="700" fontFamily="monospace" data-testid="totals-containers">{totalContainers}</text>
          <text x="14"  y="132" fill="#94a3b8" fontSize="9"  fontFamily="monospace">CONTAINERS · 40' STEEL</text>

          <text x="138" y="118" fill="#22d3ee" fontSize="22" fontWeight="700" fontFamily="monospace" data-testid="totals-living-sf">{livingSF.toLocaleString()}</text>
          <text x="138" y="132" fill="#94a3b8" fontSize="9"  fontFamily="monospace">SF LIVING</text>

          <text x="266" y="118" fill="#fde047" fontSize="22" fontWeight="700" fontFamily="monospace">31%</text>
          <text x="266" y="132" fill="#94a3b8" fontSize="9"  fontFamily="monospace">2BR MIX</text>

          <text x="14"  y="150" fill="#5b6776" fontSize="8"  fontFamily="monospace" letterSpacing="1">
            78 STUDIO · 78 1BR · 60 2BR · ELEVATOR-FREE (3 STORIES MAX)
          </text>
        </g>

        {/* PARKING card */}
        <g transform={`translate(${margin + 16 + 400}, ${H - margin - 138})`}>
          <rect width="290" height="158" fill="#0f172a" stroke="#22d3ee" strokeWidth="1.5" />
          <text x="14" y="22" fill="#22d3ee" fontSize="10" letterSpacing="2" fontFamily="monospace">
            PARKING · BELIZE SI 157/2022 REG 6(b)
          </text>
          <g fontFamily="monospace" fontSize="8.5" fill="#cbd5e1">
            <text x="14" y="40">Required (1 stall / 500 SF habitable)</text>
            <text x="276" y="40" textAnchor="end" fill="#fbbf24" fontWeight="700">{parkingReq}</text>
            <text x="14" y="54">Provided · perimeter cart + comm parking (north)</text>
            <text x="276" y="54" textAnchor="end" fill="#7dd3fc" fontWeight="700">{stripStalls}</text>
            <line x1="14" y1="60" x2="276" y2="60" stroke="#334155" strokeWidth="0.6" />
            <text x="14" y="74" fill={parkingMet ? "#22c55e" : "#ef4444"} fontWeight="700">
              {parkingMet ? "✓ COMPLIANT" : "△ SHORT BY " + (parkingReq - stripStalls)}
            </text>
            <text x="276" y="74" textAnchor="end" fill={parkingMet ? "#22c55e" : "#ef4444"} fontWeight="700">
              {`${stripStalls} / ${parkingReq}`}
            </text>
            <text x="14" y="92" fill="#86efac">⚡ EV-equipped (every 5th)</text>
            <text x="276" y="92" textAnchor="end" fill="#86efac" fontWeight="700">~20 stalls</text>
            <text x="14" y="104" fill="#10b981" fontWeight="700">🔋 BESS · 200 kWh LFP</text>
            <text x="276" y="104" textAnchor="end" fill="#10b981" fontWeight="700">night cart charging</text>
          </g>
        </g>
      </svg>
    </div>
  );
}
